import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { EmptyState, PageHeader } from "@/components/dashboard/shared";
import { getAPIBaseURL, getAdminHeaders } from "@/lib/auth";
import { cn } from "@/lib/utils";

type Conversation = {
  id: string;
  user_1: string;
  user_2: string;
  user_1_name: string;
  user_1_email: string | null;
  user_2_name: string;
  user_2_email: string | null;
  user_1_is_seller: boolean;
  user_2_is_seller: boolean;
  last_message: string | null;
  last_message_time: string | null;
  unread_count: number;
  product_name: string | null;
  product_image: string | null;
  shop_id: string | null;
  shop_name: string | null;
  shop_logo: string | null;
};

type Message = {
  id: string;
  sender_id: string;
  sender_name: string | null;
  text: string | null;
  message_type: string | null;
  media_url: string | null;
  timestamp: string;
};

type Filter = "all" | "sellers" | "buyers";

const TIME = new Intl.DateTimeFormat("en-US", { hour: "2-digit", minute: "2-digit" });
const DAY = new Intl.DateTimeFormat("en-US", { day: "2-digit", month: "short" });

function formatWhen(value: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  const today = new Date();
  const sameDay = date.toDateString() === today.toDateString();
  return sameDay ? TIME.format(date) : DAY.format(date);
}

/** Seller chat when either participant owns a shop; otherwise a buyer / user chat. */
function isSellerChat(c: Conversation): boolean {
  return c.user_1_is_seller || c.user_2_is_seller;
}

function initials(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function Thumb({ src, alt }: { src: string | null; alt: string }) {
  if (!src) {
    return <div className="grid size-9 shrink-0 place-items-center rounded-lg bg-muted text-xs text-muted-foreground">—</div>;
  }
  return <img src={src} alt={alt} className="size-9 shrink-0 rounded-lg object-cover" loading="lazy" />;
}

function messagePreview(m: Message): string {
  if (m.text && m.text.trim()) return m.text;
  if (m.message_type && m.message_type !== "text") return `[${m.message_type}]`;
  return "";
}

async function getJSON<T>(path: string): Promise<T> {
  const response = await fetch(`${getAPIBaseURL()}${path}`, { headers: getAdminHeaders() });
  if (!response.ok) throw new Error(`Request failed (${response.status})`);
  return response.json() as Promise<T>;
}

export function MessagesPage() {
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [filter, setFilter] = React.useState<Filter>("all");
  const [search, setSearch] = React.useState("");

  const conversationsQuery = useQuery({
    queryKey: ["admin-conversations"],
    queryFn: () => getJSON<{ conversations: Conversation[] }>("/api/v1/admin/conversations"),
    refetchInterval: 60_000,
  });
  const conversations = conversationsQuery.data?.conversations ?? [];

  const visible = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    return conversations.filter((c) => {
      if (filter === "sellers" && !isSellerChat(c)) return false;
      if (filter === "buyers" && isSellerChat(c)) return false;
      if (!term) return true;
      return [c.user_1_name, c.user_1_email, c.user_2_name, c.user_2_email, c.last_message]
        .some((value) => (value ?? "").toLowerCase().includes(term));
    });
  }, [conversations, filter, search]);

  // Keep a valid selection: the chosen chat, else the first visible one.
  const selected = visible.find((c) => c.id === selectedId) ?? visible[0] ?? null;

  const messagesQuery = useQuery({
    queryKey: ["admin-messages", selected?.id],
    queryFn: () => getJSON<{ messages: Message[] }>(`/api/v1/admin/conversations/${selected!.id}/messages`),
    enabled: !!selected,
  });
  const messages = messagesQuery.data?.messages ?? [];

  const unread = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);
  const sellerChats = conversations.filter(isSellerChat).length;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Messages"
        description="Review conversations between customers and sellers. Replies are sent from the app by the people themselves."
      />

      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardContent className="pt-6">
            <p className="text-xs font-semibold uppercase text-muted-foreground">Conversations</p>
            <p className="mt-2 text-3xl font-bold">{conversations.length.toLocaleString("en-US")}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-xs font-semibold uppercase text-muted-foreground">Seller chats</p>
            <p className="mt-2 text-3xl font-bold">{sellerChats.toLocaleString("en-US")}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-xs font-semibold uppercase text-muted-foreground">Unread messages</p>
            <p className="mt-2 text-3xl font-bold">{unread.toLocaleString("en-US")}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="overflow-hidden">
        <div className="grid h-[640px] max-h-[calc(100dvh-18rem)] min-h-[480px] lg:grid-cols-[340px_minmax(0,1fr)]">
          <aside className="flex min-h-0 flex-col border-b lg:border-b-0 lg:border-r">
            <div className="space-y-3 border-b p-4">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="pl-9"
                  placeholder="Search by name, email or message"
                  aria-label="Search conversations"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div role="tablist" aria-label="Conversation type" className="flex gap-2">
                {(
                  [
                    ["all", "All"],
                    ["sellers", "Sellers"],
                    ["buyers", "Buyers"],
                  ] as [Filter, string][]
                ).map(([value, label]) => (
                  <Button
                    key={value}
                    size="sm"
                    variant={filter === value ? "default" : "outline"}
                    role="tab"
                    aria-selected={filter === value}
                    onClick={() => setFilter(value)}
                  >
                    {label}
                  </Button>
                ))}
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto" role="listbox" aria-label="Conversations">
              {conversationsQuery.isLoading && <p className="p-4 text-sm text-muted-foreground">Loading conversations…</p>}
              {conversationsQuery.isError && (
                <p className="p-4 text-sm text-destructive">Could not load conversations. Refresh the page to try again.</p>
              )}
              {!conversationsQuery.isLoading && !conversationsQuery.isError && visible.length === 0 && (
                <p className="p-4 text-sm text-muted-foreground">
                  {conversations.length === 0 ? "No conversations yet." : "No conversation matches this search."}
                </p>
              )}
              {visible.map((c) => {
                const active = selected?.id === c.id;
                const title = c.user_1_name === c.user_2_name ? c.user_1_name : `${c.user_1_name} & ${c.user_2_name}`;
                return (
                  <button
                    key={c.id}
                    role="option"
                    aria-selected={active}
                    onClick={() => setSelectedId(c.id)}
                    className={cn(
                      "grid w-full grid-cols-[auto_minmax(0,1fr)_auto] gap-3 border-b p-4 text-left hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      active && "bg-primary/5",
                    )}
                  >
                    <Avatar>
                      <AvatarFallback>{initials(c.user_1_name)}</AvatarFallback>
                    </Avatar>
                    <span className="min-w-0">
                      <span className="flex items-center gap-2">
                        <strong className="truncate text-sm">{title}</strong>
                        {isSellerChat(c) && (
                          <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">Seller</span>
                        )}
                      </span>
                      <span className="mt-1 block truncate text-xs font-medium text-foreground/80">
                        {c.shop_name ?? "No shop"} · {c.product_name ?? "No product"}
                      </span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {c.last_message || "No messages yet"}
                      </span>
                    </span>
                    <span className="text-right text-[10px] text-muted-foreground">
                      {formatWhen(c.last_message_time)}
                      {c.unread_count > 0 && (
                        <span className="mt-2 grid size-5 place-items-center rounded-full bg-primary text-primary-foreground">
                          {c.unread_count}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </aside>

          <section className="flex min-h-0 min-w-0 flex-col">
            {!selected ? (
              <div className="flex flex-1 items-center justify-center p-8">
                <EmptyState title="Select a conversation" message="Choose a conversation on the left to read it." />
              </div>
            ) : (
              <>
                <header className="border-b p-4">
                  <h3 className="truncate font-semibold">
                    {selected.user_1_name} <span className="text-muted-foreground">·</span> {selected.user_2_name}
                  </h3>
                  <p className="truncate text-xs text-muted-foreground">
                    {[selected.user_1_email, selected.user_2_email].filter(Boolean).join(" · ") || "No email on file"}
                  </p>
                  <div className="mt-3 flex min-w-0 items-center gap-3 rounded-xl bg-muted/40 p-2.5">
                    <Thumb src={selected.shop_logo} alt={selected.shop_name ?? "Shop"} />
                    <div className="min-w-0">
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Shop</p>
                      <p className="truncate text-sm font-semibold">{selected.shop_name ?? "No shop"}</p>
                    </div>
                    <div className="mx-2 h-8 w-px bg-border" />
                    <Thumb src={selected.product_image} alt={selected.product_name ?? "Product"} />
                    <div className="min-w-0">
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Product</p>
                      <p className="truncate text-sm font-semibold">{selected.product_name ?? "No product"}</p>
                    </div>
                  </div>
                </header>

                <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4" aria-live="polite">
                  {messagesQuery.isLoading && <p className="text-sm text-muted-foreground">Loading messages…</p>}
                  {messagesQuery.isError && (
                    <p className="text-sm text-destructive">Could not load this conversation.</p>
                  )}
                  {!messagesQuery.isLoading && !messagesQuery.isError && messages.length === 0 && (
                    <p className="text-sm text-muted-foreground">No messages in this conversation.</p>
                  )}
                  {messages.map((m) => {
                    const fromFirst = m.sender_id === selected.user_1;
                    return (
                      <div key={m.id} className={cn("flex", fromFirst ? "justify-start" : "justify-end")}>
                        <div className={cn("max-w-[75%] rounded-2xl px-4 py-2 text-sm", fromFirst ? "bg-muted" : "bg-primary/10")}>
                          <p className="mb-1 text-[11px] font-semibold text-muted-foreground">
                            {m.sender_name || (fromFirst ? selected.user_1_name : selected.user_2_name)}
                          </p>
                          {messagePreview(m) && <p className="whitespace-pre-wrap break-words">{messagePreview(m)}</p>}
                          {m.media_url && (
                            <a href={m.media_url} target="_blank" rel="noreferrer" className="text-xs text-primary underline">
                              Open attachment
                            </a>
                          )}
                          <p className="mt-1 text-right text-[10px] text-muted-foreground">{formatWhen(m.timestamp)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <p className="border-t bg-muted/40 px-4 py-3 text-xs text-muted-foreground">
                  Read-only. Admins can review conversations here; replies come from the Deuxda app.
                </p>
              </>
            )}
          </section>
        </div>
      </Card>
    </div>
  );
}
